import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '../../api/client'
import { remoteConnectShellQuery, type RemoteConnectShell } from '../../api/settingsRemoteConnect'
import { Forbidden } from '../../ui/Forbidden'
import { Spinner } from '../../ui/Spinner'
import { RustdeskOrgPanels, RustdeskProviderServers } from '../remote_connect/RustdeskOrgPanels'
import { RustdeskProviderSettings } from './RustdeskSettingsPage'

/**
 * Settings -> General -> RustDesk si RustDesk servers: cele doua intrari din
 * meniul din stanga al setarilor, imediat sub AI Agent (`SettingsMenu`, zona
 * General, din `application/Services/Navigation`).
 *
 *   /settings/rustdesk          setarile organizatiei: instalarea (At start /
 *                               On demand), IP-urile permise, serverele
 *                               organizatiei, starea pe aparate, How RustDesk
 *                               is secured;
 *   /settings/rustdesk_servers  serverele si setarile furnizorului.
 *
 * Istoria: partea organizatiei statea sub bifele ScreenConnect din Remote
 * Connect, partea furnizorului pe ecranul `/settings/rustdesk`. Pe 6 octombrie
 * 2026 s-au mutat ca file in bara orizontala a paginii de firma; pe 7
 * octombrie Roxana le-a cerut in meniul din stanga, la General, sub AI Agent.
 * Adresele de pe 6 octombrie (`/settings/companies/rustdesk[_servers]`)
 * trimit aici (`router.tsx`).
 *
 * API-UL E ACELASI. Organizatia citeste si scrie tot prin
 * `/settings/remote_connect` (+ `/rustdesk/*`), furnizorul tot prin
 * `/settings/rustdesk`; dreptul e cel de pana acum (`settings.my_company.read`,
 * scrierea `.edit`) si il hotaraste serverul - un 403 se vede ca refuz.
 */

type Saved = RemoteConnectShell & { saved: boolean }

/** Raspunsul unei scrieri e ecranul intreg: il pune direct in cache. */
function useTakeShell() {
  const query = useQueryClient()
  return (next: Saved) => {
    query.setQueryData(remoteConnectShellQuery.queryKey, next)
    void query.invalidateQueries({ queryKey: ['settings', 'remote_connect'] })
  }
}

/** Settings -> General -> RustDesk: setarile organizatiei. */
export function RustdeskOrgSettingsPage() {
  const shell = useQuery(remoteConnectShellQuery)
  const take = useTakeShell()

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  return (
    <div className="page-content-wrapper" data-page="rustdesk-settings">
      <div id="content">
        <RustdeskOrgPanels section={shell.data.rustdesk} canWrite={shell.data.canWrite} onSaved={take} />
      </div>
    </div>
  )
}

/** Settings -> General -> RustDesk servers: serverele si setarile furnizorului. */
export function RustdeskServersSettingsPage() {
  const shell = useQuery(remoteConnectShellQuery)
  const query = useQueryClient()
  const take = useTakeShell()

  return (
    <div className="page-content-wrapper" data-page="rustdesk-servers-settings">
      <RustdeskProviderSettings
        servers={
          shell.data ? (
            <RustdeskProviderServers
              section={shell.data.rustdesk}
              canWrite={shell.data.canWrite}
              onSaved={(next) => {
                take(next)
                /* Primul server e randul din setarile furnizorului: o schimbare
                   a lui se vede si in cardul "Default server". */
                void query.invalidateQueries({ queryKey: ['settings', 'rustdesk'] })
              }}
            />
          ) : null
        }
      />
    </div>
  )
}
